import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

interface DraftGuardContextValue {
  confirmNavigation: () => boolean
  isDirty: boolean
  setDirty: (dirty: boolean) => void
}

const DraftGuardContext = createContext<DraftGuardContextValue | undefined>(undefined)

export const DraftGuardProvider = ({ children }: { children: React.ReactNode }) => {
  const [isDirty, setDirty] = useState(false)
  const confirmNavigation = useCallback(() => {
    if (!isDirty) return true
    return window.confirm('You have unsaved changes. Leave this page and discard them?')
  }, [isDirty])

  const value = useMemo(() => ({ confirmNavigation, isDirty, setDirty }), [confirmNavigation, isDirty])
  return <DraftGuardContext.Provider value={value}>{children}</DraftGuardContext.Provider>
}

export const useDraftGuard = () => {
  const context = useContext(DraftGuardContext)
  if (!context) throw new Error('useDraftGuard must be used inside DraftGuardProvider.')
  return context
}

export const useUnsavedChangesGuard = (isDirty: boolean) => {
  const { setDirty } = useDraftGuard()

  useEffect(() => {
    setDirty(isDirty)
    return () => setDirty(false)
  }, [isDirty, setDirty])

  useEffect(() => {
    if (!isDirty) return
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [isDirty])
}
